/**
 * Composer preview (spec 42 R9, design 43 AD3).
 *
 * Mounts the **real** storefront section components — the same
 * `@flint/ui/store-sections` code `apps/store` renders — inside the ERP page.
 * A look-alike preview is worse than none: it drifts, and then it lies about
 * what buyers see. `data-testid="section-<type>"` appears on both surfaces so
 * a test can prove they are the same component.
 *
 * The frame is an **iframe**, matching the theme-editor wireframe: the store
 * renders at real size (readable — no shrink-to-fit transform), and the
 * iframe's own width is the viewport its media queries see, so the desktop /
 * tablet / mobile buttons produce genuinely accurate reflows rather than a
 * scaled-down picture. The ERP's stylesheets are cloned into the iframe head so
 * the storefront CSS + fonts resolve; storefront colours still come from the
 * `.flint-store-theme` scoped token twin, set on the mounted wrapper.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { HomeSections, StoreHeader, StoreFooter } from "@flint/ui/store-sections";
import "@flint/ui/store-sections.css";
import "@flint/ui/store-tokens-scoped.css";
import {
  themeVars,
  themeBaseSize,
  defaultStoreTheme,
  type HomeSection,
  type StoreTheme,
} from "@flint/api-contract/store-home";
import { PreviewCard } from "./preview-card.tsx";
import {
  sampleProductImage,
  sampleProductFallback,
  SAMPLE_HERO_IMAGES,
  SAMPLE_FABRIC_IMAGE,
} from "./sample-images.ts";

/** Preview device — controlled by the editor toolbar (module 22 UI). */
export type PreviewDevice = "desktop" | "tablet" | "mobile";

/**
 * The frame's max width, which becomes the iframe viewport. Desktop fills the
 * canvas up to a comfortable page width (the wireframe's 1180); tablet and
 * mobile are true device widths so the real breakpoints (768 / 1024) fire.
 */
const FRAME_MAX_WIDTH: Record<PreviewDevice, number> = {
  desktop: 1180,
  tablet: 820,
  mobile: 390,
};

/**
 * Document zoom per device. Desktop zooms out so ~3 sections read at once
 * (and the widened layout viewport keeps it a true desktop); tablet and mobile
 * stay 1:1 so they mirror a real device.
 */
const FRAME_ZOOM: Record<PreviewDevice, number> = {
  desktop: 0.68,
  tablet: 1,
  mobile: 1,
};

/**
 * Realistic wholesale dummy listings, cycled to fill any grid size.
 *
 * "Product 1 · Sign in to view price" repeated eight times made every layout
 * look like a lorem-ipsum page — a merchant judging a template (or their own
 * draft) needs tiles that read like a real catalogue. Names, pack lines and
 * prices are plainly generic garments; the caption under the preview still
 * declares everything illustrative.
 */
const SAMPLE_LISTINGS = [
  { name: "Classic Crew Tee", packLine: "8-pack · S–XXL", priceLabel: "₹2,400 / pack" },
  { name: "Oxford Shirt — Slim", packLine: "6-pack · M–XL", priceLabel: "₹3,900 / pack" },
  { name: "Kids Polo — Assorted", packLine: "12-pack · 2–10y", priceLabel: "₹4,080 / pack" },
  { name: "Straight-Fit Chino", packLine: "6-pack · 30–38", priceLabel: "₹5,340 / pack" },
  { name: "Rib-Knit Kurti", packLine: "6-pack · S–XL", priceLabel: "₹3,540 / pack" },
  { name: "Hooded Sweatshirt", packLine: "5-pack · M–XXL", priceLabel: "₹4,750 / pack" },
  { name: "Printed Nightwear Set", packLine: "10-pack · Free size", priceLabel: "₹3,900 / pack" },
  { name: "Denim Jacket — Washed", packLine: "4-pack · M–XL", priceLabel: "₹6,360 / pack" },
  { name: "Cotton Track Pant", packLine: "8-pack · M–XXL", priceLabel: "₹3,120 / pack" },
  { name: "Linen Blend Shirt", packLine: "6-pack · M–XXL", priceLabel: "₹5,100 / pack" },
] as const;

const sampleListings = (n: number) =>
  Array.from({ length: n }, (_, i) => ({
    slug: `sample-${i}`,
    imageUrl: sampleProductImage(i),
    // The silhouette PreviewCard swaps in if the photo CDN is unreachable.
    imageFallback: sampleProductFallback(i),
    ...SAMPLE_LISTINGS[i % SAMPLE_LISTINGS.length]!,
  }));

/**
 * Stand-in collections for a store that has none yet — also what the template
 * preview tab hands the header nav, so its menu isn't empty.
 */
export const SAMPLE_COLLECTIONS = [
  { slug: "new-arrivals", name: "New Arrivals" },
  { slug: "core-range", name: "Core Range" },
  { slug: "festive-edit", name: "Festive Edit" },
];

/**
 * Attach placeholder catalog data to a config-shaped section.
 *
 * Also dresses empty image slots with the placeholder art from
 * `sample-images.ts` — **render-side only**. A merchant-authored `imageUrl`
 * always wins; only absent/blank slots are filled, and nothing injected here
 * can reach a save (spec 42 R6 owns what may be persisted).
 */
function withSampleData(collections: Array<{ slug: string; name: string }>) {
  return (section: HomeSection): HomeSection => {
    switch (section.type) {
      case "product_grid":
        return { ...section, listings: sampleListings(section.limit) } as HomeSection;
      case "collection_rail": {
        // Bind before the callback — TS drops the narrowing inside a closure.
        const source = section.source;
        const pool =
          source.kind === "all-visible"
            ? collections
            : collections.filter((c) => c.slug === source.slug);
        // A store with no collections yet still gets rails, so the seller can
        // see what the section will look like once they make some.
        const rails = (pool.length > 0 ? pool : SAMPLE_COLLECTIONS).map(
          (c) => ({ ...c, type: "core", listingCount: 4, listings: sampleListings(4) }),
        );
        return { ...section, rails } as HomeSection;
      }
      case "category_grid": {
        const tiles = (collections.length > 0 ? collections : SAMPLE_COLLECTIONS)
          .slice(0, section.limit)
          .map((c, i) => ({ ...c, imageUrl: sampleProductImage(i), listingCount: 4 }));
        return { ...section, tiles } as HomeSection;
      }
      case "hero_slider": {
        // A slide with no uploaded image gets an abstract banner, cycled so a
        // multi-slide hero shows variety. The hero paints white text over an
        // image, and the banners are deep-toned for exactly that.
        const slides = section.slides.map((slide, i) =>
          slide.imageUrl
            ? slide
            : { ...slide, imageUrl: SAMPLE_HERO_IMAGES[i % SAMPLE_HERO_IMAGES.length]! },
        );
        return { ...section, slides } as HomeSection;
      }
      case "image_text":
        return section.imageUrl
          ? section
          : ({ ...section, imageUrl: SAMPLE_FABRIC_IMAGE } as HomeSection);
      case "gallery": {
        const images = section.images.map((src, i) => src || sampleProductImage(i));
        return { ...section, images } as HomeSection;
      }
      default:
        return section;
    }
  };
}

/**
 * A same-origin `about:blank` iframe whose `<head>` mirrors the ERP document's
 * stylesheets, with `children` portalled into its `<body>`. Rendering into a
 * separate document is what gives the preview its own viewport (real media
 * queries) at real size.
 */
function PreviewFrame({
  children,
  className,
  zoom,
  rootFontSize,
}: {
  children: ReactNode;
  className?: string;
  zoom: number;
  /** Storefront base font size (px) — set on the iframe root so rem scales. */
  rootFontSize: number;
}) {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const [body, setBody] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const doc = ref.current?.contentDocument;
    if (!doc) return;
    // Mirror every stylesheet from the ERP document — storefront section CSS,
    // scoped tokens and @font-face rules — so the preview matches the live
    // store. Cloning (not moving) leaves the ERP's own styles untouched.
    doc.head.replaceChildren(
      ...Array.from(
        document.querySelectorAll('head style, head link[rel="stylesheet"]'),
        (node) => node.cloneNode(true),
      ),
    );
    // The ERP shell pins `html, body { overflow: hidden }` (its own scroll lives
    // in inner panes). That rule rides along in the clone and would clip the
    // preview so it can't scroll — so re-open scrolling on the iframe root, and
    // leave heights at auto so the document scrolls naturally and the sticky
    // header pins to the iframe viewport. `!important` beats the cloned rule.
    doc.documentElement.style.setProperty("overflow-y", "auto", "important");
    doc.documentElement.style.setProperty("overflow-x", "hidden", "important");
    doc.body.style.setProperty("overflow", "visible", "important");
    doc.body.style.setProperty("height", "auto", "important");
    doc.body.style.margin = "0";
    setBody(doc.body);
  }, []);

  // Zoom the whole document (like a browser's zoom-out) so more of the page
  // fits in view. `zoom` — unlike `transform` — keeps the document scrollable
  // and the sticky header working, and it widens the layout viewport the media
  // queries see (physical ÷ zoom), so a zoomed-out desktop stays a desktop.
  useEffect(() => {
    const doc = ref.current?.contentDocument;
    if (doc) doc.documentElement.style.setProperty("zoom", String(zoom));
  }, [zoom, body]);

  // Base font size on the iframe root, so every rem in the preview scales the
  // same way the live storefront's `:root{font-size}` scales it.
  useEffect(() => {
    const doc = ref.current?.contentDocument;
    if (doc) doc.documentElement.style.fontSize = `${rootFontSize}px`;
  }, [rootFontSize, body]);

  return (
    <>
      <iframe
        ref={ref}
        title="Storefront preview"
        className={className}
        style={{ border: 0, display: "block", background: "var(--bg)" }}
      />
      {body ? createPortal(children, body) : null}
    </>
  );
}

export function ComposerPreview({
  sections,
  storeName,
  profile,
  storeSlug,
  collections,
  phone,
  theme = defaultStoreTheme,
  device = "desktop",
}: {
  sections: HomeSection[];
  collections: Array<{ slug: string; name: string }>;
  storeName: string;
  profile: {
    establishedYear: number | null;
    teamSize: string | null;
    monthlyCapacity: string | null;
    city: string | null;
    about: string | null;
  };
  storeSlug: string;
  /**
   * Contact number, so a WhatsApp CTA previews exactly as buyers see it.
   * Passing null here was preview drift: the CTA vanished in the composer but
   * rendered on the live page.
   */
  phone: string | null;
  /** Per-store appearance — overrides the scope's default tokens live. */
  theme?: StoreTheme;
  /** Which frame width to render — the toolbar owns this now. */
  device?: PreviewDevice;
}) {
  // The composer holds *configuration*; the storefront's payload also carries
  // each section's resolved catalog data. Rather than query the catalog here,
  // the preview attaches representative sample data so the arrangement — how
  // many tiles, in what grid, under which heading — is what's on show. The
  // caption below says so, so nothing here pretends to be real stock.
  const enabled = sections.filter((s) => s.enabled).map(withSampleData(collections));

  return (
    <div className="mx-auto flex h-full w-full flex-col" style={{ maxWidth: FRAME_MAX_WIDTH[device] }}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-paper shadow-[0_12px_40px_rgba(20,25,40,0.16)]">
        {/* Browser chrome — a cue that this is the buyer's page, not our UI. */}
        <div className="flex h-9 shrink-0 items-center gap-2 border-b border-line bg-raised px-3.5">
          <span className="flex gap-1.5" aria-hidden>
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
          </span>
          <span className="ml-1.5 flex h-6 min-w-0 max-w-[340px] flex-1 items-center gap-1.5 rounded-full border border-line bg-paper px-3 text-[11px] text-muted">
            <span aria-hidden>🔒</span>
            <span className="truncate">store.flint.app/s/{storeSlug || "your-store"}</span>
          </span>
        </div>
        <PreviewFrame
          className="min-h-0 w-full flex-1"
          zoom={FRAME_ZOOM[device]}
          rootFontSize={themeBaseSize(theme)}
        >
          {/* `inert` keeps preview links and buttons out of the tab order and
              un-clickable — it's a picture of a page — while the iframe itself
              still scrolls. Full page: header, sections, footer, the same
              components the storefront renders, so the preview can't drift. */}
          <div
            className="flint-store-theme"
            aria-label="Storefront preview"
            // `inert` set imperatively — it's a boolean DOM property, and
            // passing it as a JSX attribute trips React's empty-string warning.
            ref={(el) => {
              if (el) el.inert = true;
            }}
            style={{
              ...(themeVars(theme) as CSSProperties),
              minHeight: "100vh",
              backgroundColor: "var(--bg)",
              color: "var(--ink)",
              fontFamily: "var(--font-sans)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <StoreHeader
              storeName={storeName}
              storeSlug={storeSlug}
              logoUrl={theme.logoUrl}
              logoHeight={theme.logoHeight}
              config={theme.header}
              collections={collections}
              phone={phone}
              accountSlot={
                <span
                  style={{
                    padding: "0.375rem 0.75rem",
                    borderRadius: "var(--r-sm)",
                    border: "1px solid var(--accent)",
                    color: "var(--accent)",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                  }}
                >
                  Sign in
                </span>
              }
            />
            <div style={{ flex: 1 }}>
              {enabled.length === 0 ? (
                <p style={{ padding: "2rem", color: "var(--muted)", fontSize: "0.875rem" }}>
                  Every section is hidden — buyers would see your store's default layout.
                </p>
              ) : (
                <HomeSections
                  sections={enabled as never}
                  storeName={storeName}
                  profile={profile}
                  ctx={{ storeSlug, phone }}
                  renderCard={(listing) => (
                    <PreviewCard
                      {...(listing as {
                        name?: string;
                        packLine?: string;
                        priceLabel?: string;
                        imageUrl?: string;
                        imageFallback?: string;
                      })}
                    />
                  )}
                  // Mirror the storefront's <Container> exactly (max-width 72rem,
                  // 1.5rem inline padding) + the 2rem block rhythm, so spacing
                  // in the preview equals the live page.
                  wrap={(node, key, fullBleed) =>
                    fullBleed ? (
                      <div key={key}>{node}</div>
                    ) : (
                      <div
                        key={key}
                        style={{
                          width: "100%",
                          maxWidth: "var(--s-container, 72rem)",
                          marginInline: "auto",
                          paddingInline: "1.5rem",
                        }}
                      >
                        <div style={{ paddingBlock: "var(--s-section-gap, 2rem)" }}>{node}</div>
                      </div>
                    )
                  }
                />
              )}
            </div>
            <StoreFooter
              storeName={storeName}
              storeSlug={storeSlug}
              {...(theme.logoUrl ? { logoUrl: theme.logoUrl } : {})}
              collections={collections}
              config={theme.footer}
              contact={{ phone, city: profile.city, gstin: null }}
              year={2026}
            />
          </div>
        </PreviewFrame>
      </div>
      <p className="mt-1.5 shrink-0 text-center text-xs text-muted">
        Product images and prices are illustrative here — the live store shows your real catalog.
      </p>
    </div>
  );
}
